/**
 * VendorHealthStrip — compact 32px-tall status strip (Iter 62.7).
 *
 * Industry-standard pattern: status bar is "persistent but ignorable".
 * Just colored dots + names, full details on hover. Replaces the previous
 * tall pill-row that ate ~60px of vertical real-estate on every page.
 *
 * Polls /api/ai/ops/vendor-health every 60s.
 */
import { useEffect, useState, useCallback } from 'react';
import { governanceAPI } from '../../../lib/governanceApi';
import { RefreshCw } from 'lucide-react';
import { StatusDot } from '../design';

const POLL_MS = 60_000;

// Map vendor health statuses to our universal StatusDot vocabulary.
const STATUS_NORMALIZE = {
  ok: 'ok',
  degraded: 'degraded',
  down: 'down',
  not_configured: 'not_configured',
};

export default function VendorHealthStrip() {
  const [health, setHealth] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchHealth = useCallback(async (force = false) => {
    try {
      if (force) setRefreshing(true);
      const res = await governanceAPI.getVendorHealth(force);
      setHealth(res.data);
    } catch (_e) {
      // silent — non-critical
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchHealth(false);
    const t = setInterval(() => fetchHealth(false), POLL_MS);
    return () => clearInterval(t);
  }, [fetchHealth]);

  if (!health) {
    return (
      <div
        className="gov-text-tertiary flex h-8 items-center gap-2 border-b border-[var(--gov-border)] bg-[var(--gov-bg-surface)] px-4 text-[11px]"
        data-testid="vendor-health-strip-loading"
      >
        <span className="gov-skeleton h-1.5 w-1.5 rounded-full" />
        Checking third-party vendors…
      </div>
    );
  }

  return (
    <div
      className="flex h-8 items-center gap-4 border-b border-[var(--gov-border)] bg-[var(--gov-bg-surface)] px-4 text-[11px]"
      data-testid="vendor-health-strip"
    >
      <span className="gov-text-tertiary font-medium uppercase tracking-wider">Vendors</span>
      <div className="flex flex-wrap items-center gap-3">
        {health.vendors.map((v) => {
          const status = STATUS_NORMALIZE[v.status] || 'unknown';
          return (
            <span
              key={v.name}
              className="inline-flex items-center gap-1.5"
              title={`${v.name} · ${v.status} · ${v.latency_ms}ms · ${v.message || ''}`}
              data-testid={`vendor-pill-${v.name.toLowerCase().replace(/\W/g, '-')}`}
            >
              <StatusDot status={status} variant="dot" size="xs" label={null} />
              <span className="gov-text-secondary">{v.name}</span>
              {v.status === 'ok' && (
                <span className="gov-text-tertiary gov-tabular">{v.latency_ms}ms</span>
              )}
            </span>
          );
        })}
      </div>
      <button
        onClick={() => fetchHealth(true)}
        disabled={refreshing}
        className="gov-text-tertiary ml-auto inline-flex items-center gap-1 rounded px-1.5 py-0.5 transition-colors hover:bg-[var(--gov-bg-elevated)] hover:text-[var(--gov-text-primary)] disabled:opacity-50"
        data-testid="vendor-health-refresh"
        title="Force refresh (bypass 60s cache)"
      >
        <RefreshCw className={`h-3 w-3 ${refreshing ? 'animate-spin' : ''}`} />
      </button>
    </div>
  );
}
